import { DateTime } from 'luxon'
import type { SnEvent } from './types'

/**
 * `YYYY-MM-DD` for an event, in the event's OWN zone.
 *
 * Must match how `groupGamesByDay` keys games (venue-local), or an evening
 * event whose UTC instant falls on the next day is filed in the cell beside
 * the one holding the games it belongs with. Falls back to the viewer's zone
 * when the event carries none, which is what the detail screens display in too.
 *
 * Deliberately the same rule `mergeEventsIntoDayBuckets` applies for the list
 * view, so the calendar and the list can never file one event on two days.
 */
export const eventDayKey = (event: SnEvent): string => {
  const startsAt = event?.starts_at
  if (!startsAt) {
    return ''
  }
  const dt = event.timezone
    ? DateTime.fromISO(startsAt, { zone: event.timezone })
    : DateTime.fromISO(startsAt)
  return dt.isValid ? dt.toFormat('yyyy-MM-dd') : ''
}

/** Millis for ordering. Undated entries sort last rather than throwing. */
const startMillis = (startsAt?: string | null): number => {
  const time = startsAt ? new Date(startsAt).getTime() : NaN
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time
}

/**
 * One renderable entry in a calendar cell — a game or an event.
 *
 * The layouts (month, week, day, agenda) all render a heterogeneous list, so
 * they take this union rather than two parallel arrays. `game` stays `unknown`
 * for the same reason `DayBucketLike` does: this file must not depend on the
 * game feature's types, which live on the platform side.
 */
export type CalendarEntry =
  | { kind: 'game'; game: unknown }
  | { kind: 'event'; event: SnEvent }

/**
 * Interleaves events into a day's already-ordered games by start time.
 *
 * A game wins a tie, so a game's position in a cell never shifts as events
 * load in beside it — the same stable tie-break the list view uses.
 */
export const interleaveByStart = (
  games: unknown[],
  events: SnEvent[]
): CalendarEntry[] => {
  const entries: Array<{ time: number; entry: CalendarEntry }> = [
    ...games.map((game) => ({
      time: startMillis((game as { starts_at?: string | null })?.starts_at),
      entry: { kind: 'game' as const, game },
    })),
    ...events.map((event) => ({
      time: startMillis(event?.starts_at),
      entry: { kind: 'event' as const, event },
    })),
  ]

  entries.sort((a, b) => {
    if (a.time !== b.time) {
      return a.time - b.time
    }
    if (a.entry.kind === b.entry.kind) {
      return 0
    }
    return a.entry.kind === 'game' ? -1 : 1
  })

  return entries.map(({ entry }) => entry)
}

/** Groups events by their own civil day, for cheap per-cell lookup. */
export const bucketEventsByDay = (
  events: SnEvent[] | null | undefined
): Map<string, SnEvent[]> => {
  const byDay = new Map<string, SnEvent[]>()

  ;(events ?? []).forEach((event) => {
    const dayKey = eventDayKey(event)
    if (dayKey === '') {
      return
    }
    const existing = byDay.get(dayKey)
    if (existing) {
      existing.push(event)
    } else {
      byDay.set(dayKey, [event])
    }
  })

  return byDay
}

/** A stable React key for an entry. */
export const calendarEntryKey = (entry: CalendarEntry): string =>
  entry.kind === 'event'
    ? `event-${entry.event.id}`
    : `game-${(entry.game as { id?: string })?.id ?? ''}`
